import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Star } from 'lucide-react';
import { useProductReviews, useHasUserReviewed, useProductReviewStats } from '@/hooks/useUserReviews';
import { ReviewCard } from './ReviewCard';
import { ReviewForm } from './ReviewForm';
import { useAuth } from '@/contexts/AuthContext';
import { isFeatureEnabled } from '@/lib/featureFlags';

interface UserReviewsSectionProps {
  productId: string;
  productTitle: string;
}

/**
 * User Reviews Section for ProductDetail page
 * Shows approved reviews, review form, and statistics
 */
export function UserReviewsSection({ productId, productTitle }: UserReviewsSectionProps) {
  const { user } = useAuth();
  const [showForm, setShowForm] = useState(false);
  
  const { data: reviews, isLoading: reviewsLoading } = useProductReviews(productId);
  const { data: stats, isLoading: statsLoading } = useProductReviewStats(productId);
  const { data: hasReviewed } = useHasUserReviewed(user?.id, productId);

  if (!isFeatureEnabled('userReviews')) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">Reviews feature coming soon!</p>
      </div>
    );
  }

  if (reviewsLoading || statsLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  const hasReviews = reviews && reviews.length > 0;
  const canWriteReview = user && !hasReviewed;

  return (
    <div className="space-y-8">
      {/* Stats Overview */}
      {hasReviews && stats && (
        <div className="grid md:grid-cols-2 gap-6">
          {/* Average Rating */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="text-center">
                  <div className="text-5xl font-bold">{stats.averageRating.toFixed(1)}</div>
                  <div className="flex justify-center gap-1 mt-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star
                        key={star}
                        className={`h-4 w-4 ${
                          star <= Math.round(stats.averageRating)
                            ? 'fill-yellow-400 text-yellow-400'
                            : 'text-muted-foreground'
                        }`}
                      />
                    ))}
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    {stats.totalReviews} {stats.totalReviews === 1 ? 'review' : 'reviews'}
                  </p>
                </div>

                {/* Rating Distribution */}
                <div className="flex-1 space-y-2">
                  {[5, 4, 3, 2, 1].map((rating) => {
                    const count = stats.ratingDistribution[rating];
                    const percentage = stats.totalReviews > 0 
                      ? (count / stats.totalReviews) * 100 
                      : 0;

                    return (
                      <div key={rating} className="flex items-center gap-2">
                        <span className="text-sm w-8">{rating}</span>
                        <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                        <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-yellow-400 transition-all"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                        <span className="text-sm text-muted-foreground w-8 text-right">
                          {count}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Write Review CTA */}
          <Card className="bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20">
            <CardContent className="pt-6">
              <h3 className="font-semibold text-lg mb-2">Share Your Experience</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Help other agents by sharing your experience with {productTitle}
              </p>
              {canWriteReview ? (
                <Button 
                  onClick={() => setShowForm(!showForm)}
                  className="w-full"
                >
                  {showForm ? 'Cancel' : 'Write a Review'}
                </Button>
              ) : hasReviewed ? (
                <p className="text-sm text-muted-foreground text-center py-2">
                  You've already reviewed this product
                </p>
              ) : (
                <Button onClick={() => window.location.href = '/auth'} variant="outline" className="w-full">
                  Sign in to write a review
                </Button>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Review Form (if user wants to write) */}
      {showForm && canWriteReview && (
        <ReviewForm
          productId={productId}
          userId={user.id}
          onSuccess={() => setShowForm(false)}
        />
      )}

      {/* Reviews List */}
      {hasReviews ? (
        <div className="space-y-6">
          <h3 className="text-2xl font-bold">Customer Reviews</h3>
          <div className="space-y-4">
            {reviews.map((review) => (
              <ReviewCard key={review.id} review={review} />
            ))}
          </div>
        </div>
      ) : (
        <Card>
          <CardContent className="py-16 text-center space-y-4">
            <p className="text-muted-foreground mb-4">
              No reviews yet. Be the first to review {productTitle}!
            </p>
            {canWriteReview && (
              <Button onClick={() => setShowForm(true)}>
                Write the First Review
              </Button>
            )}
            {!user && (
              <Button onClick={() => window.location.href = '/auth'} variant="outline">
                Sign in to write a review
              </Button>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
